import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRM, type PaymentMethod, type PreviewSaleResponse } from '@bazaar/shared';
import { useCart } from '../context/CartContext.js';
import { useToast } from '../context/ToastContext.js';
import { api, ApiError } from '../lib/api.js';
import { BigButton } from '../components/BigButton.js';
import { QtyStepper } from '../components/QtyStepper.js';
import { MoneyInput } from '../components/MoneyInput.js';
import { SegmentedControl } from '../components/SegmentedControl.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { Page, PageHeader, Split } from '../components/layout.js';

const PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'qr', label: 'QR' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

interface InsufficientStockDetail {
  productId: number;
  name: string;
  available: number;
  requested: number;
}

export function CartPage() {
  const navigate = useNavigate();
  const cart = useCart();
  const { showToast } = useToast();

  const [editingPriceFor, setEditingPriceFor] = useState<number | null>(null);
  const [pendingRemove, setPendingRemove] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cancelSaleOpen, setCancelSaleOpen] = useState(false);
  // The server's own preview of the automatic discount (spec §5) - debounced
  // ~250ms after any cart change. Null means "no preview yet" or "the
  // preview call failed" - either way the row is hidden and the sale still
  // proceeds normally; the server applies the real discount at confirm time.
  const [preview, setPreview] = useState<PreviewSaleResponse | null>(null);
  const previewRequestIdRef = useRef(0);

  const { subtotalCents, discountCents: clampedDiscountCents, totalCents, itemCount } = cart.totals;

  useEffect(() => {
    if (cart.state.lines.length === 0) {
      setPreview(null);
      return;
    }
    const requestId = ++previewRequestIdRef.current;
    const timer = window.setTimeout(() => {
      void api
        .previewSale({
          items: cart.state.lines.map((l) => ({
            productId: l.productId,
            qty: l.qty,
            ...(l.unitPriceCents !== l.defaultPriceCents ? { unitPriceCents: l.unitPriceCents } : {}),
          })),
          discountCents: cart.state.discountCents,
          paymentMethod: cart.state.paymentMethod,
          note: cart.state.note.trim(),
        })
        .then((res) => {
          if (previewRequestIdRef.current === requestId) setPreview(res);
        })
        .catch(() => {
          if (previewRequestIdRef.current === requestId) setPreview(null);
        });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [cart.state.lines, cart.state.discountCents, cart.state.paymentMethod]);

  const autoDiscount = preview?.autoDiscount ?? null;
  // Once the preview is in, the manual discount is capped at subtotal minus
  // the automatic one - before that (or if the preview call failed), fall
  // back to the pre-feature cap of the plain subtotal; the server enforces
  // the real cap regardless (DISCOUNT_EXCEEDS_SUBTOTAL at confirm time).
  const maxManualDiscountCents = preview?.maxManualDiscountCents ?? subtotalCents;
  const displayTotalCents = preview?.totalCents ?? totalCents;

  // `discountInputValid` is false while the discount field holds text that
  // doesn't parse to a valid amount, or that exceeds the subtotal -
  // MoneyInput reports `null` to the reducer for both (see `SET_DISCOUNT`
  // in cartReducer.ts). `cart.discountValid` also catches a *previously*
  // valid, committed discount going stale (e.g. a cart line was removed
  // and the old discount no longer fits) - both disable Confirm and show
  // an error, and `cart.totals` already reports "no discount" for either.
  const discountError = !cart.state.discountInputValid
    ? 'Enter a valid discount, up to the subtotal after the automatic discount'
    : !cart.discountValid || cart.state.discountCents > maxManualDiscountCents
      ? 'Discount cannot exceed the subtotal after the automatic discount'
      : undefined;
  const canSubmit = cart.state.lines.length > 0 && cart.discountValid && !submitting;

  function handleDecrement(productId: number, qty: number) {
    if (qty <= 1) {
      setPendingRemove(productId);
      return;
    }
    cart.decrementItem(productId);
  }

  function handleCancelSale() {
    // Nothing is sent to the server - this just discards the in-progress
    // sale locally. `cart.clear()` resets state to `initialCartState`, and
    // CartContext's own effect persists that reset straight to
    // sessionStorage, so there's no separate storage-clearing step needed.
    cart.clear();
    setCancelSaleOpen(false);
    showToast('Sale cancelled', 'info');
    navigate('/sell');
  }

  async function handleConfirmSale() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const sale = await api.createSale({
        items: cart.state.lines.map((l) => ({
          productId: l.productId,
          qty: l.qty,
          ...(l.unitPriceCents !== l.defaultPriceCents ? { unitPriceCents: l.unitPriceCents } : {}),
        })),
        discountCents: cart.state.discountCents,
        paymentMethod: cart.state.paymentMethod,
        note: cart.state.note.trim(),
      });
      cart.clear();
      showToast(`Sale #${sale.id} recorded · ${formatRM(sale.totalCents)}`, 'success');
      navigate('/sell');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INSUFFICIENT_STOCK') {
        const details = (err.details?.products as InsufficientStockDetail[] | undefined) ?? [];
        for (const d of details) {
          cart.updateStock(d.productId, d.available);
        }
        // err.message is already the server's "Only N left of X" / "X is out
        // of stock" wording for whichever product actually failed.
        setSubmitError(err.message);
        showToast(err.message, 'error');
      } else if (err instanceof ApiError && err.code === 'DISCOUNT_EXCEEDS_SUBTOTAL') {
        // Defence in depth: the UI should already block this (discountValid),
        // but if the subtotal changed server-side between page load and
        // submit, surface the API's own message rather than a generic one.
        setSubmitError(err.message);
        showToast(err.message, 'error');
      } else if (err instanceof ApiError) {
        setSubmitError(err.message);
        showToast(err.message, 'error');
      } else {
        showToast('Something went wrong. Please try again.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (cart.state.lines.length === 0) {
    return (
      <EmptyState
        title="Your cart is empty"
        description="Add products from the Sell screen to start a sale."
        action={
          <BigButton onClick={() => navigate('/sell')} className="mt-2">
            Back to Sell
          </BigButton>
        }
      />
    );
  }

  return (
    <Page
      // The fixed "Confirm sale" bar (65px bottom-tab offset + its own
      // padding/button height on phones) can otherwise cover the last line
      // item. Reserve enough space for it plus the device's safe-area
      // inset. At `lg` the bar sits inline inside the summary card (no
      // longer fixed), so only a little breathing room is needed.
      className="pb-[calc(6rem+env(safe-area-inset-bottom,0px))] lg:pb-8"
    >
      <PageHeader title="Review sale" />

      <Split
        aside={
          <div className="card card-stack">
            {autoDiscount && (
              <div className="rounded-lg bg-aqua px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body font-semibold text-base text-teal-dark truncate">{autoDiscount.label}</p>
                  <p className="font-body text-sm text-teal-dark">Applied automatically</p>
                </div>
                <p className="font-heading font-semibold text-lg text-teal-dark shrink-0">
                  &minus;{formatRM(autoDiscount.cents)}
                </p>
              </div>
            )}

            <MoneyInput
              id="discount"
              label="Discount (RM)"
              valueCents={cart.state.discountCents}
              onChangeCents={(cents) => {
                // MoneyInput reports `null` both for unparsable text and for
                // a well-formed amount over `max`. Forward that straight to
                // the reducer either way: it never coerces an invalid draft
                // to 0 (that used to silently turn "typed a discount bigger
                // than the subtotal" into "no discount at all, full price
                // charged"), and it flags the discount as not in effect so
                // `cart.totals` shows "no discount applied" instead of the
                // previous valid one.
                cart.setDiscountCents(cents);
              }}
              max={maxManualDiscountCents}
              error={discountError}
            />

            <div>
              <p className="field-label mb-2">Payment method</p>
              <SegmentedControl
                options={PAYMENT_OPTIONS}
                value={cart.state.paymentMethod}
                onChange={cart.setPaymentMethod}
                ariaLabel="Payment method"
              />
            </div>

            <div>
              <label htmlFor="note" className="field-label block mb-1">
                Note (optional)
              </label>
              <textarea
                id="note"
                value={cart.state.note}
                onChange={(e) => cart.setNote(e.target.value)}
                maxLength={280}
                rows={2}
                className="w-full rounded-lg border-2 border-border px-4 py-2 text-base font-body"
              />
            </div>

            <dl className="flex flex-col gap-1 pt-2 border-t border-border">
              <div className="flex justify-between font-body text-base text-text">
                <dt>Subtotal</dt>
                <dd>{formatRM(subtotalCents)}</dd>
              </div>
              {autoDiscount && (
                <div className="flex justify-between font-body text-base text-text">
                  <dt>Automatic discount</dt>
                  <dd>&minus;{formatRM(autoDiscount.cents)}</dd>
                </div>
              )}
              {clampedDiscountCents > 0 && (
                <div className="flex justify-between font-body text-base text-text">
                  <dt>Discount</dt>
                  <dd>&minus;{formatRM(clampedDiscountCents)}</dd>
                </div>
              )}
              <div className="flex justify-between font-heading font-semibold text-[28px] text-ink pt-1">
                <dt>Total</dt>
                <dd data-testid="cart-total">{formatRM(displayTotalCents)}</dd>
              </div>
            </dl>

            {submitError && (
              <p role="alert" className="font-body text-sm text-danger">
                {submitError}
              </p>
            )}

            <div className="fixed bottom-[65px] inset-x-0 z-30 bg-surface border-t border-border lg:static lg:z-auto lg:border-0 lg:bg-transparent">
              <div className="page-container py-4 lg:px-0 lg:py-0">
                <BigButton className="w-full" disabled={!canSubmit} onClick={() => void handleConfirmSale()}>
                  {submitting ? 'Recording sale…' : `Confirm sale · ${itemCount} item${itemCount === 1 ? '' : 's'}`}
                </BigButton>
              </div>
            </div>
          </div>
        }
      >
        <ul className="list-stack">
          <li>
            <BigButton
              variant="outline-danger"
              className="w-full"
              onClick={() => setCancelSaleOpen(true)}
            >
              Cancel sale
            </BigButton>
          </li>
          {cart.state.lines.map((line) => (
            <li key={line.productId} className="card-row flex gap-3">
              <div className="h-16 w-16 shrink-0 rounded-lg bg-teal-dark flex items-center justify-center overflow-hidden">
                {line.thumbUrl ? (
                  <img src={line.thumbUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="block w-full max-w-full truncate text-white font-heading font-bold text-sm px-1 text-center">
                    {line.code}
                  </span>
                )}
              </div>

              <div className="flex-1 flex flex-col gap-2 min-w-0">
                <div>
                  <p className="font-body font-semibold text-base text-text truncate">{line.name}</p>
                  <p className="font-body text-sm text-muted">{line.code}</p>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <QtyStepper
                    qty={line.qty}
                    max={line.stockQty}
                    onIncrement={() => cart.incrementItem(line.productId)}
                    onDecrement={() => handleDecrement(line.productId, line.qty)}
                    label={line.name}
                  />

                  {editingPriceFor === line.productId ? (
                    <div className="flex items-center gap-2">
                      <MoneyInput
                        id={`price-${line.productId}`}
                        label="Unit price"
                        valueCents={line.unitPriceCents}
                        onChangeCents={(cents) => {
                          if (cents !== null) cart.setPriceOverride(line.productId, cents);
                        }}
                        className="w-28"
                      />
                      <button
                        type="button"
                        onClick={() => setEditingPriceFor(null)}
                        className="font-body font-semibold text-base text-teal-dark min-h-12 px-2"
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingPriceFor(line.productId)}
                      className="font-heading font-semibold text-teal-dark underline decoration-dotted text-base min-h-12"
                    >
                      {formatRM(line.unitPriceCents)} each
                    </button>
                  )}
                </div>

                <p className="font-heading font-semibold text-lg text-ink text-right">
                  {formatRM(line.unitPriceCents * line.qty)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Split>

      <ConfirmDialog
        open={pendingRemove !== null}
        title="Remove item?"
        description="This will remove the item from the cart."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => {
          if (pendingRemove !== null) cart.removeItem(pendingRemove);
          setPendingRemove(null);
        }}
        onCancel={() => setPendingRemove(null)}
      />

      <ConfirmDialog
        open={cancelSaleOpen}
        title="Cancel this sale?"
        description="Everything in the cart will be cleared."
        confirmLabel="Cancel sale"
        cancelLabel="Keep cart"
        variant="danger"
        onConfirm={handleCancelSale}
        onCancel={() => setCancelSaleOpen(false)}
      />
    </Page>
  );
}
